import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { environment } from '@base/environments/environment';
import { UserSessionService } from '../../../services/user-session.service';
import { canAccessDashboard } from '../../dashboard.guard';
import {
  clearPostLoginRedirectState,
  markPostLoginRedirectApplied,
  markUserSession,
} from '../../post-login-redirect.guard';
import { LoginFlow } from '../login-flow';
import { AgencyModulesService } from './agency-modules.service';
import { ClientFingerprintService } from './client-fingerprint.service';
import {
  GestionLoginApiResponse,
  mapGestionContextToLoggedUser,
} from './logged-user.mapper';

@Injectable()
export class NewLoginService implements LoginFlow {
  private readonly authUrl = `${environment.authApiBaseUrl}/v1/gestion`;

  constructor(
    private readonly http: HttpClient,
    private readonly fingerprintService: ClientFingerprintService,
    private readonly agencyModulesService: AgencyModulesService,
    private readonly userSessionService: UserSessionService,
    private readonly router: Router,
  ) {}

  shouldShowForm(): boolean {
    return true;
  }

  onInit(): void {
    void this.fingerprintService.get().catch(() => undefined);
  }

  async signIn(username: string, password: string): Promise<void> {
    const response = await this.login(username, password);
    const agencyModules = await this.agencyModulesService.getModuleIds(
      response.context.agencyId,
      response.idToken,
    );
    const loggedUser = mapGestionContextToLoggedUser(
      response.context,
      response.idToken,
      agencyModules,
    );
    this.userSessionService.loggedUser = loggedUser;
    this.userSessionService.clearMigrationMark();

    // A fresh login decides the landing page here, so the layout and the
    // /home guard must not apply the post-login redirect again
    clearPostLoginRedirectState();
    markUserSession(loggedUser);
    markPostLoginRedirectApplied();
    await this.router.navigate([
      canAccessDashboard(loggedUser) ? '/dashboard' : '/home',
    ]);
  }

  async login(
    username: string,
    password: string,
  ): Promise<GestionLoginApiResponse> {
    return firstValueFrom(
      this.http.post<GestionLoginApiResponse>(
        `${this.authUrl}/login`,
        { username, password },
        { withCredentials: true, headers: await this.buildAuthHeaders() },
      ),
    );
  }

  async logout(): Promise<void> {
    await firstValueFrom(
      this.http.post(
        `${this.authUrl}/logout`,
        {},
        { withCredentials: true, headers: await this.buildAuthHeaders() },
      ),
    );
  }

  private async buildAuthHeaders(): Promise<HttpHeaders> {
    const fingerprint = await this.fingerprintService.get();
    return new HttpHeaders({ fingerprint });
  }
}
